
import React from 'react';
import { Zone, ZoneType } from '../types';
import { MoveDiagonal } from 'lucide-react';

interface ZoneNodeProps {
  zone: Zone;
  isSelected?: boolean;
  isSimple?: boolean;
  onMouseDown?: (e: React.MouseEvent, zoneId: string) => void;
  onResizeStart?: (e: React.MouseEvent, zoneId: string) => void;
}

export const ZoneNode: React.FC<ZoneNodeProps> = ({ 
  zone, 
  isSelected, 
  isSimple = false,
  onMouseDown, 
  onResizeStart 
}) => {
  
  const getStyle = (): React.CSSProperties => {
    let bg = '#f8fafc';
    let border = '#cbd5e1';
    let pattern = 'none';

    // If a custom color is set, use it for background.
    if (zone.customColor) {
      bg = zone.customColor;
      border = 'rgba(0,0,0,0.2)'; 
    } else {
      switch (zone.type) {
        case ZoneType.LOCKER_ROOM:
          bg = isSimple ? '#f1f5f9' : '#e2e8f0';
          border = isSimple ? '#94a3b8' : '#94a3b8';
          pattern = isSimple ? 'none' : 'repeating-linear-gradient(45deg, #e2e8f0, #e2e8f0 10px, #cbd5e1 10px, #cbd5e1 20px)';
          break;
        case ZoneType.LOUNGE:
          bg = isSimple ? '#fffaf5' : '#ffedd5'; 
          border = '#fdba74';
          break;
        case ZoneType.ENTRANCE:
          bg = 'rgba(255, 255, 255, 0.5)';
          border = '#94a3b8';
          break;
        case ZoneType.OBSTACLE:
          bg = isSimple ? '#475569' : '#1e293b'; 
          border = '#0f172a';
          break;
        case ZoneType.CUSTOM:
          bg = isSimple ? '#f0f9ff' : '#e0f2fe'; 
          border = '#7dd3fc';
          break;
        case ZoneType.SPORT_AREA:
        default:
          bg = '#fff';
          border = '#e2e8f0';
          break;
      }
    }

    if (isSelected) {
      border = '#3b82f6'; 
    }

    if (isSimple) {
      border = isSelected ? '#3b82f6' : '#cbd5e1';
    }

    return {
      position: 'absolute',
      left: zone.x,
      top: zone.y,
      width: zone.width,
      height: zone.height,
      background: pattern !== 'none' ? pattern : bg,
      backgroundColor: pattern !== 'none' ? undefined : bg,
      border: `${isSimple ? '1px' : '2px'} solid ${border}`, 
      borderRadius: isSimple ? '0px' : '0.25rem', 
      boxSizing: 'border-box',
      zIndex: 1, 
      cursor: onMouseDown ? 'grab' : 'default',
    };
  };

  return (
    <div
      style={getStyle()}
      onMouseDown={(e) => onMouseDown?.(e, zone.id)}
      onClick={(e) => e.stopPropagation()} 
      className="flex items-start justify-start p-2 relative group overflow-hidden"
    >
      <span className={`text-xs font-bold uppercase tracking-wide select-none ${zone.type === ZoneType.OBSTACLE ? 'text-slate-400' : 'text-slate-700/60'} ${isSimple ? 'font-sans' : ''}`} style={{ color: zone.type === ZoneType.OBSTACLE ? undefined : 'rgba(0,0,0,0.5)' }}>
        {zone.name}
      </span>

      {/* Resize Handle (Bottom Right) */}
      {isSelected && onResizeStart && (
        <div
          className={`absolute bottom-0 right-0 w-6 h-6 cursor-nwse-resize flex items-center justify-center bg-blue-500 text-white ${isSimple ? '' : 'rounded-tl'}`}
          onMouseDown={(e) => onResizeStart(e, zone.id)}
        >
           <div className={`w-2 h-2 bg-white ${isSimple ? 'rounded-none' : 'rounded-full'}`} />
        </div>
      )}
    </div>
  );
};
